import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi'
import { z } from 'zod'
import { OverrideIconTypeSchema, VisibleIfSchema } from './BaseFormQuestionAndAnswerSchemas'

extendZodWithOpenApi(z)

/**
 * Per-instance fields shared by every variant — what the Form Template's per-question entry
 * in FormConfig carries regardless of question type.
 *
 * TODO (Question Syncing): this should grow as we identify more fields that every question type
 * needs to define at the instance level.
 */
export const BaseQuestionInstanceSchema = z.object({
  id: z.string(),
  visibleIf: VisibleIfSchema.optional(),
  title: z.string().optional(),
  description: z.string().optional(),
})
export type BaseQuestionInstance = z.infer<typeof BaseQuestionInstanceSchema>

/**
 * Fields **intrinsic** to a Question: properties of the Question itself that should
 * be consistent across all instances. Persisted on `question_versions.config`.
 *
 * TODO (Question Syncing): this should shrink as we identify which fields are truly intrinsic
 * and should be the same across all instances.
 */
export const BaseQuestionDefinitionSchema = z.object({
  isRequired: z.boolean(),
  name: z.string(),
  readOnly: z.boolean().optional(),
  disabled: z.boolean().optional(),
  weighting: z.number().optional(),
  tooltip: z.string().optional(),
  doubleEntryEnabled: z.boolean().optional(),
  overrideIconType: OverrideIconTypeSchema.optional(),
  isScorable: z.boolean().optional(),
  autoFlaggedScoreCategoryIds: z.array(z.string().uuid()).optional(),
  isMaterial: z.boolean().optional(),
})
export type BaseQuestionDefinition = z.infer<typeof BaseQuestionDefinitionSchema>

/**
 * Fields that are **overrideable** per Form Template instance. Persisted on
 * `question_versions.config` as a default (required here), and mirrored on
 * `BaseQuestionInstanceSchema` as optional override slots.
 *
 * TODO (Question Syncing): this should grow as we identify more fields that are instance related,
 * but can have an overrideable default value.
 */
export const BaseQuestionConfigSchema = z.object({
  visibleIf: VisibleIfSchema,
  title: z.string(),
  description: z.string().optional(),
})
export type BaseQuestionConfig = z.infer<typeof BaseQuestionConfigSchema>

/**
 * Link from a Form Template's per-question entry to an Organisation Question. `versionId` is
 * set when the Form Template is pinned to a specific Version; absent means "track latest".
 */
export const OrganisationQuestionLinkSchema = z.object({
  id: z.string().uuid(),
  versionId: z.number().int().positive().optional(),
})
export type OrganisationQuestionLink = z.infer<typeof OrganisationQuestionLinkSchema>

/**
 * Storage shape of a Linked entry on a Form Template's per-question array — carries the
 * per-instance fields plus the link payload. No `type`, no variant-specific fields; those are
 * resolved at render time from the linked Question's Definition + Config.
 */
export const LinkedFormQuestionSchema = BaseQuestionInstanceSchema.extend({
  organisationQuestion: OrganisationQuestionLinkSchema,
}).strict()
export type LinkedFormQuestion = z.infer<typeof LinkedFormQuestionSchema>
